import { minuteOffsetToTime } from '@/components/events/helpers/minute-offset-to-time.helper';
import { calculateFlowDurationFromSlots, isUntimedSlot } from '@/components/flows/detail/slot-list/slot-timing';
import type { FlowDto, FlowSlotDto } from '@/types/dtos/flows/response/flow.dto';

export type RundownSegmentTiming = {
  startTime?: string;
  durationLabel?: string;
};

function durationLabelFromMinutes(minutes: number | undefined): string | undefined {
  if (minutes === undefined || !Number.isFinite(minutes)) {
    return undefined;
  }
  return `${Math.max(0, Math.round(minutes))} min`;
}

function resolveSlotSegmentTiming(slot: FlowSlotDto, flow: FlowDto | undefined): RundownSegmentTiming {
  const duration =
    slot.computedDurationMinutes ??
    slot.plannedDurationMinutes ??
    (typeof slot.content === 'object' && slot.content && 'plannedDurationMinutes' in slot.content
      ? slot.content.plannedDurationMinutes
      : undefined);
  const timing: RundownSegmentTiming = {
    durationLabel: durationLabelFromMinutes(duration),
  };
  if (isUntimedSlot(slot)) {
    return timing;
  }
  const offset = slot.computedOffsetMinutes ?? slot.plannedOffsetMinutes;
  const flowStart = flow?.slot?.startOffsetMinutes;
  if (offset !== undefined && flowStart !== undefined) {
    timing.startTime = minuteOffsetToTime(flowStart + offset);
  }
  return timing;
}

function resolveFlowBookendTiming(flow: FlowDto | undefined, edge: 'start' | 'end'): RundownSegmentTiming {
  if (!flow) {
    return {};
  }
  const flowStart = flow.slot?.startOffsetMinutes;
  const flowDuration = flow.durationPlannedMinutes ?? calculateFlowDurationFromSlots(flow.slots ?? []);
  if (edge === 'start') {
    const timing: RundownSegmentTiming = {
      durationLabel: durationLabelFromMinutes(flowDuration),
    };
    if (flowStart !== undefined) {
      timing.startTime = minuteOffsetToTime(flowStart);
    }
    return timing;
  }
  if (flowStart === undefined || flowDuration === undefined) {
    return {};
  }
  return { startTime: minuteOffsetToTime(flowStart + flowDuration) };
}

/** Clock start + duration for Control rundown chrome. Times come from the effective-flow join, not the cue sheet. */
export function resolveRundownSegmentTiming(
  segmentKind: string,
  slot: FlowSlotDto | null | undefined,
  flow: FlowDto | undefined,
): RundownSegmentTiming {
  if (segmentKind === 'session' || segmentKind === 'break') {
    if (!slot) {
      return {};
    }
    return resolveSlotSegmentTiming(slot, flow);
  }
  if (segmentKind === 'flow_intro') {
    return resolveFlowBookendTiming(flow, 'start');
  }
  if (segmentKind === 'after_flow') {
    return resolveFlowBookendTiming(flow, 'end');
  }
  return {};
}
